import React from 'react'
import { Wordmark } from '@/components/Wordmark'

/**
 * Marketing3AuthShell — the pre-auth chrome for the customer sign-in / password pages,
 * dressed in the landing3 (mkt3-*) design language so those pages read as siblings of the
 * public landing rather than the operator app.
 *
 * A full-viewport warm-stone canvas (mkt3-root paper token) with a faint teal wash, the
 * three-bar ledger glyph + wordmark above a centred mkt3-panel card, and a quiet "Back to
 * the site" link to '/'. The whole tree is wrapped in `.mkt3-root` so the marketing3.css
 * variables + component classes (mkt3-panel, mkt3-display, mkt3-label, mkt3-cta) resolve;
 * each page imports marketing3.css so the bundle is present.
 *
 * The card content (heading, fields, submit) is passed as children — each page owns its own
 * form and behaviour; this shell owns only the surrounding atmosphere and brand chrome.
 */

/** The ledger wordmark glyph — three stacked bars (copied from Marketing3Layout). */
function LedgerGlyph() {
  return (
    <span aria-hidden="true" className="flex flex-col gap-[3px]">
      <span className="h-[3px] w-5 rounded-full bg-teal-600" />
      <span className="h-[3px] w-3.5 rounded-full bg-slate-400" />
      <span className="h-[3px] w-2 rounded-full bg-stone-300" />
    </span>
  )
}

interface Marketing3AuthShellProps {
  brand: string
  children: React.ReactNode
}

export default function Marketing3AuthShell({ brand, children }: Marketing3AuthShellProps) {
  return (
    <div className="mkt3-root relative flex min-h-screen flex-col items-center justify-center px-4 py-12 sm:px-6">
      {/* Soft teal wash from the top — the landing's "pool of light", quieted for a form page. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-80 mkt3-light-glow"
      />

      <div className="relative w-full max-w-md">
        {/* Brand mark above the card — links home, matching the landing header wordmark. */}
        <div className="mkt3-rise flex justify-center">
          <a
            href="/"
            className="flex items-center gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
            aria-label={`${brand} home`}
          >
            <LedgerGlyph />
            <Wordmark brand={brand} suffix="RMS" className="mkt3-display text-[1.75rem]" />
          </a>
        </div>

        {/* The card — a light instrument panel, the sign-in form's home. */}
        <div className="mkt3-panel mkt3-rise mt-8 rounded-2xl p-7 sm:p-9" style={{ animationDelay: '60ms' }}>
          {children}
        </div>

        {/* Quiet return-to-site link. */}
        <div className="mkt3-rise mt-6 text-center" style={{ animationDelay: '120ms' }}>
          <a
            href="/"
            className="mkt3-label inline-flex items-center gap-1.5 normal-case tracking-[0.16em] text-slate-500 transition-colors hover:text-teal-700"
          >
            <span aria-hidden="true">←</span>
            Back to the site
          </a>
        </div>
      </div>
    </div>
  )
}
